import React from 'react'
import { Field, useFormikContext } from 'formik'
import * as yup from 'yup'
import { InputField, DatePicker, RadioGroupField } from 'components'
import { Legend } from '../Legend'
import { Fieldset } from 'components/Layout/Fieldset'
import { FlexInputs } from 'components/Layout/FlexInputs'
import { FormDamageReport } from '../types'
import { useTranslation } from 'react-i18next'
import { formatHtmlDateInput } from 'utils/format'

export const validationSchema = yup.object({
  reporterIsDriver: yup
    .boolean()
    .required(
      'pages.damageReports.damageReportForm.driverFields.validation.driver'
    ),
  driver: yup.object().when('reporterIsDriver', {
    is: false,
    then: yup.object().shape({
      firstName: yup
        .string()
        .required(
          'pages.damageReports.damageReportForm.driverFields.validation.firstName'
        ),
      lastName: yup
        .string()
        .required(
          'pages.damageReports.damageReportForm.driverFields.validation.lastName'
        ),
      street: yup
        .string()
        .required(
          'pages.damageReports.damageReportForm.driverFields.validation.street'
        ),
      postcode: yup
        .string()
        .required(
          'pages.damageReports.damageReportForm.driverFields.validation.postcode'
        ),
      city: yup
        .string()
        .required(
          'pages.damageReports.damageReportForm.driverFields.validation.city'
        ),
      phone: yup
        .string()
        .required(
          'pages.damageReports.damageReportForm.driverFields.validation.phoneNr'
        ),
      birthday: yup
        .string()
        .required(
          'pages.damageReports.damageReportForm.driverFields.validation.birthday'
        ),
      hadPermission: yup
        .boolean()
        .required(
          'pages.damageReports.damageReportForm.driverFields.validation.hadPermission'
        ),
      permission: yup.string().when('hadPermission', {
        is: (value: boolean | undefined) => value === false,
        then: yup
          .string()
          .required(
            'pages.damageReports.damageReportForm.driverFields.validation.permission'
          ),
        otherwise: yup.string(),
      }),
      licenseAware: yup
        .string()
        .required(
          'pages.damageReports.damageReportForm.driverFields.validation.license'
        ),
    }),
    otherwise: yup.object(),
  }),
  driverHadLicense: yup
    .boolean()
    .required(
      'pages.damageReports.damageReportForm.driverFields.validation.hadLicense'
    ),
  driverHadDrugs: yup
    .boolean()
    .required(
      'pages.damageReports.damageReportForm.driverFields.validation.hadDrugs'
    ),
  driverDrugs: yup.string().when('driverHadDrugs', {
    is: true,
    then: yup
      .string()
      .required(
        'pages.damageReports.damageReportForm.driverFields.validation.driverDrugs'
      ),
    otherwise: yup.string(),
  }),
})

export const DriverFields: React.FunctionComponent = () => {
  const {
    values: {
      reporterIsDriver,
      driverHadDrugs,
      driver: { hadPermission },
    },
  } = useFormikContext<FormDamageReport>()
  const { t } = useTranslation()
  const yesNoOptions = [
    { label: t('global.yes'), value: true },
    { label: t('global.no'), value: false },
  ]
  return (
    <>
      <Fieldset>
        <Legend>
          {t('pages.damageReports.damageReportForm.driverFields.who')}
        </Legend>
        <RadioGroupField
          name="reporterIsDriver"
          radios={[
            {
              label: t(
                'pages.damageReports.damageReportForm.driverFields.self'
              ),
              value: true,
            },
            {
              label: t(
                'pages.damageReports.damageReportForm.driverFields.other'
              ),
              value: false,
            },
          ]}
          inline
          required
        />
      </Fieldset>

      {reporterIsDriver === false && (
        <Fieldset large>
          <Legend>
            {t('pages.damageReports.damageReportForm.driverFields.driver')}
          </Legend>

          <Fieldset>
            <FlexInputs>
              <Field
                component={InputField}
                id="driverfirstName"
                name="driver.firstName"
                label={t(
                  'pages.damageReports.damageReportForm.driverFields.firstName'
                )}
                required
              />
              <Field
                component={InputField}
                id="driverLastName"
                name="driver.lastName"
                label={t(
                  'pages.damageReports.damageReportForm.driverFields.lastName'
                )}
                required
              />
            </FlexInputs>
          </Fieldset>

          <Fieldset>
            <FlexInputs>
              <Field
                component={InputField}
                id="driverStreet"
                name="driver.street"
                label={t(
                  'pages.damageReports.damageReportForm.driverFields.street'
                )}
                required
              />
              <Field
                component={InputField}
                id="driverPostcode"
                name="driver.postcode"
                label={t(
                  'pages.damageReports.damageReportForm.driverFields.postcode'
                )}
                required
              />
              <Field
                component={InputField}
                id="driverCity"
                name="driver.city"
                label={t(
                  'pages.damageReports.damageReportForm.driverFields.city'
                )}
                required
              />
            </FlexInputs>
          </Fieldset>

          <Field
            component={InputField}
            id="driverPhone"
            name="driver.phone"
            label={t('pages.damageReports.damageReportForm.driverFields.phone')}
            required
          />

          <Field
            component={DatePicker}
            id="driverBirthday"
            name="driver.birthday"
            label={t(
              'pages.damageReports.damageReportForm.driverFields.birthday'
            )}
            max={formatHtmlDateInput(Date())}
            required
          />
        </Fieldset>
      )}

      <Fieldset>
        <Legend>
          {t('pages.damageReports.damageReportForm.driverFields.validLicense')}
        </Legend>
        <RadioGroupField
          name="driverHadLicense"
          radios={yesNoOptions}
          inline
          required
        />
      </Fieldset>

      {reporterIsDriver === false && (
        <Fieldset>
          <Legend>
            {t('pages.damageReports.damageReportForm.driverFields.known')}
          </Legend>
          <RadioGroupField
            name="driver.licenseAware"
            radios={yesNoOptions}
            inline
            required
          />
        </Fieldset>
      )}

      <Fieldset>
        <Legend>
          {t('pages.damageReports.damageReportForm.driverFields.hasAlc')}
        </Legend>
        <RadioGroupField
          name="driverHadDrugs"
          radios={yesNoOptions}
          inline
          required
        />

        {driverHadDrugs === true && (
          <Field
            component={InputField}
            id="driverDrugs"
            name="driverDrugs"
            label={t('pages.damageReports.damageReportForm.driverFields.type')}
            required
          />
        )}
      </Fieldset>

      {reporterIsDriver === false && (
        <>
          <Fieldset>
            <Legend>
              {t(
                'pages.damageReports.damageReportForm.driverFields.hasPermission'
              )}
            </Legend>

            <RadioGroupField
              name="driver.hadPermission"
              radios={yesNoOptions}
              inline
              required
            />

            {hadPermission === false && (
              <Field
                component={InputField}
                id="driverPermission"
                name="driver.permission"
                label={t(
                  'pages.damageReports.damageReportForm.driverFields.withPermission'
                )}
                required
              />
            )}
          </Fieldset>
        </>
      )}
    </>
  )
}
